<template>
  <div class="watch">
    <h1 class="watch-title">{{ t('watch.title') }}</h1>
    <p class="watch-lead">{{ t('watch.lead') }}</p>

    <label v-if="rows !== null" class="filter">
      <span>{{ t('watch.filter') }}</span>
      <select v-model="filter" class="md-field">
        <option value="all">{{ t('watch.all') }}</option>
        <option value="ranked">{{ t('watch.ranked') }}</option>
        <option value="event">{{ t('watch.event') }}</option>
        <option value="casual">{{ t('watch.casual') }}</option>
      </select>
    </label>

    <p v-if="listError" class="list-error">{{ t('watch.error') }}</p>
    <p v-else-if="rows === null" class="watch-empty">{{ t('watch.connecting') }}</p>
    <p v-else-if="visibleRows.length === 0" class="watch-empty">{{ t('watch.empty') }}</p>
    <ul v-else class="watch-list">
      <li v-for="row in visibleRows" :key="row.id" class="md-panel duel">
        <p class="duel-head">
          <span
            class="mode-chip"
            :class="row.mode === 'pvp_ranked' ? 'ranked' : 'casual'"
          >
            {{ row.mode === 'pvp_ranked' ? t('watch.modeRanked') : t('watch.modeCasual') }}
          </span>
          <span class="elapsed">{{ elapsed(row.startedAt) }}</span>
        </p>
        <div
          v-for="(player, index) in row.players"
          :key="index"
          class="seat"
          :class="{ turn: row.turnSeat === index }"
        >
          <AvatarView
            :config="player.avatar"
            crop="bust"
            circle
            :size="48"
            :label="player.username"
          />
          <div class="seat-meta">
            <p class="seat-name">
              <span>{{ player.username }}</span>
              <RankBadge
                v-if="player.standing"
                :league="player.standing.league"
                :division="player.standing.division"
                :placement-remaining="player.standing.placementRemaining"
                :size="22"
              />
            </p>
            <p class="seat-sub">
              {{ player.lifePoints }} LP
              · {{ t('watch.hand') }} {{ player.hand }}
              · {{ t('watch.deck') }} {{ player.deck }}
              <template v-if="!player.connected">
                · {{ t('watch.offline') }}
              </template>
            </p>
          </div>
        </div>
        <p class="phase">
          {{ t('duel.turn', { n: row.turn }) }} · {{ phaseLabel(row.phase) }}
        </p>
        <button
          type="button"
          class="md-btn-tonal"
          :disabled="row.seated"
          @click="watchDuel(row)"
        >
          {{ row.seated ? t('watch.yours') : t('watch.watch') }}
        </button>
      </li>
    </ul>

    <router-link to="/" class="watch-back">{{ t('watch.back') }}</router-link>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import type { AvatarConfig, RankedStanding } from '@shared/index'
import { api } from '../api/http'
import { useAuthStore } from '../stores/auth'
import { useTournamentStore } from '../stores/tournament'
import { useI18n } from '../i18n/t'
import AvatarView from '../components/AvatarView.vue'
import RankBadge from '../components/RankBadge.vue'

type WatchMode = 'pvp_casual' | 'pvp_ranked' | 'pvp_event'
type Filter = 'all' | 'ranked' | 'casual' | 'event'

interface WatchPlayer {
  username: string
  lifePoints: number
  connected: boolean
  hand: number
  deck: number
  standing: RankedStanding | null
  avatar: AvatarConfig
}

interface WatchRow {
  id: string
  mode: WatchMode
  roomCode: string
  startedAt: string
  turn: number
  phase: string
  turnSeat: 0 | 1
  seated: boolean
  players: [WatchPlayer, WatchPlayer]
}

const PHASE_KEYS: Record<string, string> = {
  DP: 'duel.phaseBanner.draw',
  SP: 'duel.phaseBanner.standby',
  M1: 'duel.phaseBanner.main1',
  BP: 'duel.phaseBanner.battle',
  M2: 'duel.phaseBanner.main2',
  EP: 'duel.phaseBanner.end',
}

const { t } = useI18n()
const router = useRouter()
const auth = useAuthStore()
const tournament = useTournamentStore()

const rows = ref<WatchRow[] | null>(null)
const listError = ref(false)
const filter = ref<Filter>('all')
const now = ref(Date.now())

let poll: ReturnType<typeof setInterval> | null = null
let tick: ReturnType<typeof setInterval> | null = null

const visibleRows = computed(() => {
  const list = rows.value ?? []
  if (filter.value === 'ranked') return list.filter((row) => row.mode === 'pvp_ranked')
  if (filter.value === 'event') return list.filter((row) => row.mode === 'pvp_event')
  if (filter.value === 'casual') return list.filter((row) => row.mode === 'pvp_casual')
  return list
})

function modeChipClass(mode: WatchMode): string {
  if (mode === 'pvp_ranked') return 'ranked'
  if (mode === 'pvp_event') return 'event'
  return 'casual'
}

function modeChipLabel(mode: WatchMode): string {
  if (mode === 'pvp_ranked') return t('watch.modeRanked')
  if (mode === 'pvp_event') return t('watch.modeEvent')
  return t('watch.modeCasual')
}

function phaseLabel(phase: string): string {
  const key = PHASE_KEYS[phase]
  return key ? t(key) : phase
}

function elapsed(iso: string): string {
  const start = new Date(iso).getTime()
  if (Number.isNaN(start)) return '—'
  let sec = Math.max(0, Math.floor((now.value - start) / 1000))
  const h = Math.floor(sec / 3600)
  sec %= 3600
  const m = Math.floor(sec / 60)
  const s = sec % 60
  const mm = String(m).padStart(2, '0')
  const ss = String(s).padStart(2, '0')
  return h > 0 ? `${h}:${mm}:${ss}` : `${m}:${ss}`
}

async function load(quiet = false): Promise<void> {
  try {
    const payload = await api<{ duels: WatchRow[] }>('/duels/live', {
      token: auth.token,
      quiet,
    })
    rows.value = payload.duels
    listError.value = false
  } catch {
    if (rows.value === null) listError.value = true
  }
}

function isTournamentRoom(code: string): boolean {
  const want = code.trim().toUpperCase()
  return Boolean(
    tournament.current?.matches.some(
      (match) => (match.roomCode ?? '').toUpperCase() === want,
    ),
  )
}

function watchDuel(row: WatchRow): void {
  if (row.seated || !row.roomCode) return
  void router.push({
    name: 'online-duel',
    query: {
      mode: 'spectate',
      code: row.roomCode,
      ...(isTournamentRoom(row.roomCode) ? { from: 'tournament' } : {}),
    },
  })
}

function startLoops(): void {
  stopLoops()
  if (document.hidden) return
  void load()
  poll = setInterval(() => void load(true), 3000)
  tick = setInterval(() => {
    now.value = Date.now()
  }, 1000)
}

function stopLoops(): void {
  if (poll) clearInterval(poll)
  if (tick) clearInterval(tick)
  poll = null
  tick = null
}

function onVisibility(): void {
  if (document.hidden) stopLoops()
  else startLoops()
}

onMounted(() => {
  document.addEventListener('visibilitychange', onVisibility)
  startLoops()
})

onUnmounted(() => {
  document.removeEventListener('visibilitychange', onVisibility)
  stopLoops()
})
</script>

<style scoped>
.watch {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  min-height: calc(100dvh - var(--app-nav-offset, 64px));
  padding: 24px 16px 48px;
}

.watch-title {
  margin: 0;
  color: var(--md-sys-color-on-surface);
  font-size: 36px;
  font-weight: 400;
  text-align: center;
}

.watch-lead {
  margin: -8px 0 0;
  max-width: 28rem;
  color: var(--md-sys-color-on-surface-variant);
  font-size: 14px;
  line-height: 20px;
  text-align: center;
}

.filter {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: min(560px, 100%);
  color: var(--md-sys-color-on-surface-variant);
  font-size: 12px;
}

.md-field {
  min-height: 40px;
  padding: 8px 16px;
  border: 0;
  border-bottom: 1px solid var(--md-sys-color-outline);
  border-radius: 4px 4px 0 0;
  background: var(--md-sys-color-surface-container-high);
  color: var(--md-sys-color-on-surface);
  font: inherit;
  font-size: 14px;
}

.md-field:focus {
  outline: none;
  border-bottom-width: 2px;
  border-bottom-color: var(--md-sys-color-primary);
}

.list-error {
  margin: 0;
  color: var(--md-sys-color-error);
  font-size: 13px;
}

.watch-empty {
  margin: 0;
  color: var(--md-sys-color-on-surface-variant);
  font-size: 14px;
  text-align: center;
}

.watch-list {
  list-style: none;
  margin: 0;
  padding: 0;
  width: min(560px, 100%);
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.md-panel {
  padding: 16px;
  border-radius: 12px;
  background: var(--md-sys-color-surface-container);
  box-shadow: var(--md-elevation-1);
}

.duel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 0 0 12px;
}

.mode-chip {
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 0 10px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
}

.mode-chip.ranked {
  background: var(--md-sys-color-primary-container);
  color: var(--md-sys-color-on-primary-container);
}

.mode-chip.casual {
  background: var(--md-sys-color-secondary-container);
  color: var(--md-sys-color-on-secondary-container);
}

.mode-chip.event {
  background: var(--md-sys-color-tertiary-container);
  color: var(--md-sys-color-on-tertiary-container);
}

.elapsed {
  color: var(--md-sys-color-on-surface-variant);
  font-size: 13px;
}

.seat {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 10px;
}

.seat.turn .seat-name {
  color: var(--md-sys-color-primary);
}

.seat-meta {
  flex: 1;
  min-width: 0;
}

.seat-name {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: 16px;
  font-weight: 500;
  color: var(--md-sys-color-on-surface);
}

.seat-name span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.seat-sub,
.phase {
  margin: 4px 0 0;
  color: var(--md-sys-color-on-surface-variant);
  font-size: 13px;
}

.phase {
  margin-top: 12px;
}

.md-btn-tonal {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  margin-top: 12px;
  padding: 0 16px;
  border: 0;
  border-radius: 20px;
  background: var(--md-sys-color-surface-container-high);
  color: var(--md-sys-color-on-surface);
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.1px;
  cursor: pointer;
  transition: filter var(--md-motion-short) var(--md-motion-standard);
}

.md-btn-tonal:hover:not(:disabled) {
  filter: brightness(1.06);
}

.md-btn-tonal:disabled {
  opacity: 0.55;
  cursor: default;
}

.watch-back {
  color: var(--md-sys-color-on-surface-variant);
  font-size: 14px;
  text-decoration: none;
}

.watch-back:hover {
  color: var(--md-sys-color-on-surface);
}

@media (max-width: 720px) {
  .watch-title {
    font-size: 28px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .md-btn-tonal {
    transition: none;
  }
}
</style>
